import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownPreviewSx } from '../../components/markdownPreviewSx';
import type { Phase, Project, ProjectDocument, ProjectDocumentNote } from '../../types';
import {
  projectApi,
  phaseApi,
  projectDocumentApi,
  projectDocumentNoteApi,
  fetchProjectDocumentBlob,
} from '../../api/endpoints';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { Can } from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { RequestFormDialog } from '../FeatureRequests/RequestFormDialog';
import { useAuth } from '../../auth/AuthContext';

const PREVIEWABLE_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/markdown',
  'text/html',
];

function isPreviewable(mime: string): boolean {
  return PREVIEWABLE_MIME.includes(mime);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Bỏ phần đuôi tệp để dùng làm tiêu đề mặc định khi tải lên hàng loạt. */
function stripExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
}

/**
 * Ghi chú đầu mỗi nhóm (Tài liệu chung / một giai đoạn). Chỉ Admin thấy ô
 * nhập + nút Lưu; vai trò khác chỉ thấy nội dung ghi chú dạng văn bản (ẩn
 * hẳn nếu chưa có ghi chú, tránh khoảng trống thừa).
 */
function DocumentGroupNote({
  initialNote,
  isAdmin,
  onSave,
}: {
  initialNote: string;
  isAdmin: boolean;
  onSave: (note: string) => Promise<void>;
}): JSX.Element | null {
  const [draft, setDraft] = useState(initialNote);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initialNote);
  }, [initialNote]);

  if (!isAdmin) {
    if (!initialNote.trim()) return null;
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: 'italic', mb: 1.5, whiteSpace: 'pre-wrap' }}
      >
        {initialNote}
      </Typography>
    );
  }

  const dirty = draft !== initialNote;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
      <TextField
        size="small"
        multiline
        minRows={1}
        maxRows={4}
        fullWidth
        placeholder="Ghi chú cho nhóm này (chỉ Admin thấy ô nhập)…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <Button
        size="small"
        variant="outlined"
        disabled={!dirty || saving}
        onClick={() => void handleSave()}
      >
        {saving ? 'Đang lưu…' : 'Lưu'}
      </Button>
    </Stack>
  );
}

interface PreviewState {
  doc: ProjectDocument;
  blobUrl: string;
  /** Nội dung dạng chữ, chỉ có khi mime là text/markdown (hiện dạng văn bản thô, không render HTML). */
  text?: string;
  /**
   * Nội dung HTML thô, chỉ có khi mime là text/html. Dùng với iframe
   * `srcdoc` thay vì `src={blobUrl}`: iframe đọc blob URL qua Content-Type
   * của blob, nhưng với sandbox + blob: URL trình duyệt không áp charset
   * đáng tin cậy, gây vỡ chữ tiếng Việt. srcdoc luôn được diễn giải UTF-8
   * theo spec HTML, bất kể charset gốc.
   */
  htmlSource?: string;
}

const LEFT_WIDTH_STORAGE_KEY = 'webqn_project_documents_left_width_pct';
const DEFAULT_LEFT_PCT = 42;
const MIN_LEFT_PCT = 25;
const MAX_LEFT_PCT = 65;

/**
 * Trang quản lý tài liệu dự án (route /projects/:id/documents): xem / tải
 * lên / sửa / xoá, nhóm theo giai đoạn (phase_id null = "Tài liệu chung").
 * Bố cục 2 cột: trái là danh sách/quản lý, phải là xem trước tài liệu đang
 * chọn (không còn dialog xem trước lồng bên trong).
 */
export function ProjectDocumentsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { user, roles } = useAuth();
  const isAdmin = roles.includes('Admin');
  const canDeleteAny = usePermission('PROJECT_DOCUMENT_DELETE');

  const [project, setProject] = useState<Project | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [requestFormOpen, setRequestFormOpen] = useState(false);

  // Tỷ lệ chiều rộng cột trái (danh sách tài liệu), đơn vị %. Nhớ lại qua
  // localStorage để người dùng không phải kéo lại mỗi lần vào trang.
  const [leftWidthPct, setLeftWidthPct] = useState<number>(() => {
    try {
      const saved = Number(window.localStorage.getItem(LEFT_WIDTH_STORAGE_KEY));
      return Number.isFinite(saved) && saved >= MIN_LEFT_PCT && saved <= MAX_LEFT_PCT ? saved : DEFAULT_LEFT_PCT;
    } catch {
      return DEFAULT_LEFT_PCT;
    }
  });
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const handleDividerMouseDown = useCallback(() => {
    draggingRef.current = true;
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftWidthPct(Math.min(MAX_LEFT_PCT, Math.max(MIN_LEFT_PCT, pct)));
    };
    const handleMouseUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      setLeftWidthPct((pct) => {
        try {
          window.localStorage.setItem(LEFT_WIDTH_STORAGE_KEY, String(pct));
        } catch {
          // localStorage có thể bị chặn (chế độ ẩn danh) — bỏ qua, không chặn kéo.
        }
        return pct;
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [notes, setNotes] = useState<ProjectDocumentNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Đánh dấu đã tự mở preview đầu tiên cho project nào rồi — tránh tự mở lại
  // mỗi khi `documents`/`groups` đổi identity (ví dụ sau khi xoá/sửa tài liệu).
  const autoPreviewedProjectIdRef = useRef<string | null>(null);
  const [editTarget, setEditTarget] = useState<ProjectDocument | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editPhaseId, setEditPhaseId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [uploadMode, setUploadMode] = useState<'file' | 'link'>('file');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadPhaseId, setUploadPhaseId] = useState('');
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadExternalUrl, setUploadExternalUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const isBulkUpload = uploadFiles.length > 1;

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoadingHeader(true);
    setHeaderError(null);
    Promise.all([
      projectApi.get(id),
      phaseApi.listByProject(id, { limit: 200 }),
    ])
      .then(([p, phaseResult]) => {
        if (active) {
          setProject(p);
          setPhases([...phaseResult.items].sort((a, b) => a.order_index - b.order_index));
        }
      })
      .catch((err) => {
        if (active) setHeaderError(getApiErrorMessage(err, 'Không thể tải thông tin dự án.'));
      })
      .finally(() => {
        if (active) setLoadingHeader(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    autoPreviewedProjectIdRef.current = null;
    if (!id) {
      setDocuments([]);
      setNotes([]);
      setEditTarget(null);
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.blobUrl);
        return null;
      });
      return;
    }
    let active = true;
    setLoading(true);
    Promise.all([projectDocumentApi.list(id), projectDocumentNoteApi.list(id)])
      .then(([docList, noteList]) => {
        if (active) {
          setDocuments(docList);
          setNotes(noteList);
        }
      })
      .catch((err) => {
        if (active) {
          notify(getApiErrorMessage(err, 'Không thể tải danh sách tài liệu'), 'error');
          setDocuments([]);
          setNotes([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.blobUrl);
        return null;
      });
    };
  }, [id]);

  const noteFor = (phaseId: string | null): string =>
    notes.find((n) => n.phase_id === phaseId)?.note ?? '';

  const saveNote = async (phaseId: string | null, note: string) => {
    if (!id) return;
    try {
      const saved = await projectDocumentNoteApi.upsert(id, { phaseId, note });
      setNotes((prev) => {
        const idx = prev.findIndex((n) => n.phase_id === phaseId);
        if (idx === -1) return [...prev, saved];
        const next = [...prev];
        next[idx] = saved;
        return next;
      });
      notify('Đã lưu ghi chú', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu ghi chú'), 'error');
    }
  };

  const groups = useMemo(() => {
    const general = documents.filter((d) => d.phase_id === null);
    const byPhase = phases.map((phase) => ({
      phase,
      docs: documents.filter((d) => d.phase_id === phase.id),
    }));
    // Tài liệu trỏ tới phase_id không khớp phase nào trong `phases` (ví dụ
    // giai đoạn đã bị xoá) — vẫn phải hiển thị để không "mất" tài liệu khỏi UI.
    const orphaned = documents.filter(
      (d) => d.phase_id !== null && !phases.some((phase) => phase.id === d.phase_id),
    );
    return { general, byPhase, orphaned };
  }, [documents, phases]);

  // Tự mở xem trước tài liệu ĐẦU TIÊN xem được (theo đúng thứ tự hiển thị:
  // Tài liệu chung trước, rồi lần lượt các giai đoạn) ngay khi vào trang —
  // đỡ phải tự bấm. Chỉ làm MỘT LẦN cho mỗi dự án (autoPreviewedProjectIdRef),
  // không lặp lại sau khi người dùng tự đóng preview hoặc chọn tài liệu khác.
  useEffect(() => {
    if (loading || !id || autoPreviewedProjectIdRef.current === id) return;
    const firstPreviewable = [...groups.general, ...groups.byPhase.flatMap((g) => g.docs)].find(
      (d) => !d.external_url && isPreviewable(d.mime),
    );
    if (firstPreviewable) {
      autoPreviewedProjectIdRef.current = id;
      void openPreview(firstPreviewable);
    }
  }, [loading, id, groups]);

  const canManage = (doc: ProjectDocument): boolean => canDeleteAny || doc.uploaded_by === user?.id;

  const resetUploadForm = () => {
    setUploadTitle('');
    setUploadPhaseId('');
    setUploadFiles([]);
    setUploadExternalUrl('');
  };

  const removeStagedFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!id) return;
    if (uploadMode === 'link') {
      if (!uploadTitle.trim() || !uploadExternalUrl.trim()) return;
      setUploading(true);
      try {
        const created = await projectDocumentApi.createLink(id, {
          title: uploadTitle.trim(),
          phaseId: uploadPhaseId || null,
          externalUrl: uploadExternalUrl.trim(),
        });
        setDocuments((prev) => [{ ...created, uploaded_by_name: user?.name ?? null }, ...prev]);
        notify('Đã thêm link', 'success');
        resetUploadForm();
      } catch (err) {
        notify(getApiErrorMessage(err, 'Không thể thêm link'), 'error');
      } finally {
        setUploading(false);
      }
      return;
    }

    if (uploadFiles.length === 0) return;
    if (!isBulkUpload && !uploadTitle.trim()) return;
    setUploading(true);
    let succeeded = 0;
    let firstError: unknown = null;
    for (const file of uploadFiles) {
      const title = isBulkUpload ? stripExtension(file.name) : uploadTitle.trim();
      try {
        const created = await projectDocumentApi.upload(id, {
          file,
          title,
          phaseId: uploadPhaseId || null,
        });
        setDocuments((prev) => [{ ...created, uploaded_by_name: user?.name ?? null }, ...prev]);
        succeeded += 1;
      } catch (err) {
        firstError = firstError ?? err;
      }
    }
    setUploading(false);
    if (succeeded > 0) {
      notify(succeeded > 1 ? `Đã tải lên ${succeeded} tài liệu` : 'Đã tải lên tài liệu', 'success');
    }
    if (firstError) {
      notify(getApiErrorMessage(firstError, 'Không thể tải lên một số tài liệu'), 'error');
    }
    if (!firstError) resetUploadForm();
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const handleEnableShare = async (doc: ProjectDocument) => {
    setSharingId(doc.id);
    try {
      const updated = await projectDocumentApi.enableShare(doc.id);
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, ...updated } : d)));
      const fullUrl = `${window.location.origin}${updated.share_url}`;
      const copied = await copyToClipboard(fullUrl);
      notify(
        copied ? 'Đã tạo link chia sẻ và sao chép vào clipboard' : `Đã tạo link chia sẻ: ${fullUrl}`,
        'success',
      );
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tạo link chia sẻ'), 'error');
    } finally {
      setSharingId(null);
    }
  };

  const handleCopyShareLink = async (doc: ProjectDocument) => {
    if (!doc.share_url) return;
    const fullUrl = `${window.location.origin}${doc.share_url}`;
    const copied = await copyToClipboard(fullUrl);
    notify(copied ? 'Đã sao chép link vào clipboard' : `Link chia sẻ: ${fullUrl}`, 'success');
  };

  const handleRevokeShare = async (doc: ProjectDocument) => {
    setSharingId(doc.id);
    try {
      const updated = await projectDocumentApi.revokeShare(doc.id);
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, ...updated } : d)));
      notify('Đã thu hồi link chia sẻ', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể thu hồi link chia sẻ'), 'error');
    } finally {
      setSharingId(null);
    }
  };

  const handleRemove = async (doc: ProjectDocument) => {
    setRemovingId(doc.id);
    try {
      await projectDocumentApi.remove(doc.id);
      notify('Đã xóa tài liệu', 'success');
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa tài liệu'), 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const openEdit = (doc: ProjectDocument) => {
    setEditTarget(doc);
    setEditTitle(doc.title);
    setEditPhaseId(doc.phase_id ?? '');
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !editTitle.trim()) return;
    setSavingEdit(true);
    try {
      const updated = await projectDocumentApi.update(editTarget.id, {
        title: editTitle.trim(),
        phaseId: editPhaseId || null,
      });
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === updated.id
            ? { ...updated, uploaded_by_name: editTarget.uploaded_by_name }
            : d,
        ),
      );
      notify('Đã lưu thay đổi', 'success');
      setEditTarget(null);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu thay đổi'), 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const openPreview = async (doc: ProjectDocument) => {
    setPreviewLoading(true);
    try {
      const blob = await fetchProjectDocumentBlob(doc.id);
      const blobUrl = URL.createObjectURL(blob);
      const text = doc.mime === 'text/markdown' ? await blob.text() : undefined;
      const htmlSource = doc.mime === 'text/html' ? await blob.text() : undefined;
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.blobUrl);
        return { doc, blobUrl, text, htmlSource };
      });
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể mở xem trước'), 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  const handleDownload = async (doc: ProjectDocument) => {
    try {
      const blob = await fetchProjectDocumentBlob(doc.id);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải xuống'), 'error');
    }
  };

  const renderDocRow = (doc: ProjectDocument) => (
    <Stack
      key={doc.id}
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid="project-document-row"
      sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          ...(isPreviewable(doc.mime) && !doc.external_url
            ? { cursor: 'pointer', '&:hover .doc-title': { textDecoration: 'underline' } }
            : {}),
        }}
        onClick={
          isPreviewable(doc.mime) && !doc.external_url ? () => void openPreview(doc) : undefined
        }
      >
        <Typography variant="body2" noWrap className="doc-title">
          {doc.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {doc.external_url
            ? `Link ngoài · ${doc.uploaded_by_name ?? 'Không rõ'}`
            : `${doc.file_name} · ${formatSize(doc.size_bytes)} · ${doc.uploaded_by_name ?? 'Không rõ'}`}
        </Typography>
      </Box>
      {doc.external_url ? (
        <IconButton
          size="small"
          aria-label="Mở link"
          component="a"
          href={doc.external_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      ) : (
        <>
          {isPreviewable(doc.mime) && (
            <IconButton
              size="small"
              aria-label="Xem trước"
              disabled={previewLoading}
              onClick={() => void openPreview(doc)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" aria-label="Tải xuống" onClick={() => void handleDownload(doc)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </>
      )}
      {canManage(doc) && (
        <>
          {doc.share_url ? (
            <>
              <IconButton
                size="small"
                aria-label="Copy link chia sẻ"
                color="primary"
                disabled={sharingId === doc.id}
                onClick={() => void handleCopyShareLink(doc)}
                title="Đang chia sẻ công khai — bấm để sao chép link"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Thu hồi link chia sẻ"
                disabled={sharingId === doc.id}
                onClick={() => void handleRevokeShare(doc)}
                title="Thu hồi link chia sẻ"
              >
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <IconButton
              size="small"
              aria-label="Tạo link chia sẻ"
              disabled={sharingId === doc.id}
              onClick={() => void handleEnableShare(doc)}
              title="Tạo link chia sẻ công khai"
            >
              <IosShareIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" aria-label="Sửa" onClick={() => openEdit(doc)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Xoá"
            disabled={removingId === doc.id}
            onClick={() => void handleRemove(doc)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Stack>
  );

  if (!id) {
    return <Alert severity="error">Thiếu mã dự án.</Alert>;
  }

  if (loadingHeader) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (headerError || !project) {
    return <Alert severity="error">{headerError ?? 'Không tìm thấy dự án.'}</Alert>;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link component={RouterLink} to="/projects" underline="hover" color="inherit">
              Dự án
            </Link>
            <Typography color="text.primary">{project.name}</Typography>
          </Breadcrumbs>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {project.name}
          </Typography>
          {project.description && (
            <Typography variant="body2" color="text.secondary">
              {project.description}
            </Typography>
          )}
        </Box>
        <Can permission="FEATURE_REQUEST_CREATE">
          <Button variant="contained" onClick={() => setRequestFormOpen(true)}>
            Tạo yêu cầu chức năng
          </Button>
        </Can>
      </Stack>

      <Box
        ref={splitContainerRef}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mt: 1,
        }}
      >
        <Box sx={{ width: { xs: '100%', md: `${leftWidthPct}%` }, flexShrink: 0 }}>
          <Box sx={{ maxHeight: '80vh', overflow: 'auto', pr: 1.5 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Accordion variant="outlined" defaultExpanded sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Tài liệu chung ({groups.general.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DocumentGroupNote
                      initialNote={noteFor(null)}
                      isAdmin={isAdmin}
                      onSave={(note) => saveNote(null, note)}
                    />
                    {groups.general.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Chưa có tài liệu chung.
                      </Typography>
                    ) : (
                      groups.general.map(renderDocRow)
                    )}
                  </AccordionDetails>
                </Accordion>
                {groups.byPhase.map(({ phase, docs }) => (
                  <Accordion key={phase.id} variant="outlined" sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {phase.name} ({docs.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <DocumentGroupNote
                        initialNote={noteFor(phase.id)}
                        isAdmin={isAdmin}
                        onSave={(note) => saveNote(phase.id, note)}
                      />
                      {docs.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Chưa có tài liệu trong giai đoạn này.
                        </Typography>
                      ) : (
                        docs.map(renderDocRow)
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
                {groups.orphaned.length > 0 && (
                  <Accordion variant="outlined" sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Giai đoạn đã xoá ({groups.orphaned.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{groups.orphaned.map(renderDocRow)}</AccordionDetails>
                  </Accordion>
                )}

                <Can permission="PROJECT_DOCUMENT_CREATE">
                  <Stack
                    spacing={1.5}
                    sx={{
                      mt: 3,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">Thêm tài liệu mới</Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={uploadMode}
                      onChange={(_e, value) => {
                        if (value) setUploadMode(value);
                      }}
                    >
                      <ToggleButton value="file">Tệp</ToggleButton>
                      <ToggleButton value="link">Link ngoài</ToggleButton>
                    </ToggleButtonGroup>
                    {(uploadMode === 'link' || !isBulkUpload) && (
                      <TextField
                        label="Tiêu đề"
                        size="small"
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                      />
                    )}
                    <TextField
                      select
                      label="Giai đoạn"
                      size="small"
                      value={uploadPhaseId}
                      onChange={(e) => setUploadPhaseId(e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Tài liệu chung</MenuItem>
                      {phases.map((phase) => (
                        <MenuItem key={phase.id} value={phase.id}>
                          {phase.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    {uploadMode === 'link' ? (
                      <TextField
                        label="Link diagram / tài liệu ngoài (Figma, Miro, Lucidchart, ...)"
                        size="small"
                        placeholder="https://..."
                        value={uploadExternalUrl}
                        onChange={(e) => setUploadExternalUrl(e.target.value)}
                      />
                    ) : (
                      <>
                        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                          {uploadFiles.length === 0
                            ? 'Chọn tệp (có thể chọn nhiều)'
                            : uploadFiles.length === 1
                              ? uploadFiles[0].name
                              : `Đã chọn ${uploadFiles.length} tệp — chọn thêm`}
                          <input
                            type="file"
                            hidden
                            multiple
                            onChange={(e) => {
                              const picked = Array.from(e.target.files ?? []);
                              if (picked.length === 0) return;
                              setUploadFiles((prev) => [...prev, ...picked]);
                              e.target.value = '';
                            }}
                          />
                        </Button>
                        {uploadFiles.length > 0 && (
                          <Stack spacing={0.5}>
                            {uploadFiles.map((file, index) => (
                              <Stack
                                key={`${file.name}-${index}`}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ py: 0.25 }}
                              >
                                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatSize(file.size)}
                                </Typography>
                                <IconButton
                                  size="small"
                                  aria-label="Bỏ tệp này"
                                  onClick={() => removeStagedFile(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                      </>
                    )}
                    <Button
                      variant="contained"
                      disabled={
                        uploading ||
                        (uploadMode === 'link'
                          ? !uploadTitle.trim() || !uploadExternalUrl.trim()
                          : uploadFiles.length === 0 || (!isBulkUpload && !uploadTitle.trim()))
                      }
                      onClick={() => void handleUpload()}
                    >
                      {uploading
                        ? 'Đang xử lý…'
                        : uploadMode === 'link'
                          ? 'Thêm link'
                          : uploadFiles.length > 1
                            ? `Tải lên ${uploadFiles.length} tệp`
                            : 'Tải lên'}
                    </Button>
                  </Stack>
                </Can>
              </>
            )}
          </Box>
        </Box>

        {/* Thanh kéo chia cột — chỉ hiện ở màn hình md+ (xs xếp chồng, không cần kéo). */}
        <Box
          onMouseDown={handleDividerMouseDown}
          sx={{
            display: { xs: 'none', md: 'flex' },
            width: 10,
            flexShrink: 0,
            cursor: 'col-resize',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover > div, &:active > div': { bgcolor: 'primary.main' },
          }}
        >
          <Box sx={{ width: 3, height: '90%', borderRadius: 1, bgcolor: 'divider' }} />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Paper
            variant="outlined"
            sx={{ position: 'sticky', top: 16, height: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            {preview === null ? (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {previewLoading ? 'Đang tải xem trước…' : 'Chọn một tài liệu bên trái để xem'}
                </Typography>
              </Box>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {preview.doc.title}
                  </Typography>
                  <Button size="small" onClick={closePreview}>
                    Đóng
                  </Button>
                </Stack>
                <Box sx={{ flexGrow: 1, minHeight: 0, p: 2, overflow: 'auto' }}>
                  {preview.doc.mime === 'application/pdf' ? (
                    <Box
                      component="iframe"
                      src={preview.blobUrl}
                      title={preview.doc.title}
                      sx={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  ) : preview.doc.mime === 'text/html' ? (
                    // sandbox="" = không cấp quyền nào (không allow-scripts, không
                    // allow-same-origin) — HTML do user upload có thể chứa <script>
                    // tuỳ ý, phải chặn hoàn toàn, chỉ hiện nội dung/layout tĩnh.
                    // srcdoc (không phải src={blobUrl}) vì iframe sandbox + blob: URL
                    // không áp charset đáng tin cậy — srcdoc luôn UTF-8 theo spec.
                    <Box
                      component="iframe"
                      sandbox=""
                      srcDoc={preview.htmlSource}
                      title={preview.doc.title}
                      sx={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  ) : preview.doc.mime === 'text/markdown' ? (
                    <Box
                      sx={{
                        height: '100%',
                        overflow: 'auto',
                        ...markdownPreviewSx,
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.text}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={preview.blobUrl}
                      alt={preview.doc.title}
                      sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block', mx: 'auto' }}
                    />
                  )}
                </Box>
                {preview.doc.share_url && (
                  <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      startIcon={<OpenInNewIcon />}
                      component="a"
                      href={`${window.location.origin}${preview.doc.share_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Mở link chia sẻ
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <RequestFormDialog
        open={requestFormOpen}
        request={null}
        initialProjectId={id}
        onClose={() => setRequestFormOpen(false)}
        onSaved={() => setRequestFormOpen(false)}
      />

      <Dialog open={editTarget !== null} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Sửa tài liệu</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tiêu đề"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              autoFocus
            />
            <TextField
              select
              label="Giai đoạn"
              value={editPhaseId}
              onChange={(e) => setEditPhaseId(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Tài liệu chung</MenuItem>
              {phases.map((phase) => (
                <MenuItem key={phase.id} value={phase.id}>
                  {phase.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Hủy</Button>
          <Button
            variant="contained"
            disabled={!editTitle.trim() || savingEdit}
            onClick={() => void handleSaveEdit()}
          >
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
